import React, { useMemo } from "react";
import { FileText, Download, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { ukDate } from "../../lib/format";
import { Card, EmptyState, QuietButton } from "../UI";
import { STATUS_TONE, STATUS_LABEL, COUNT_ORDER } from "./importShared";

export function StatusChip({ status, superseded }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded border font-mono uppercase ${STATUS_TONE[status] || STATUS_TONE.discarded}`}
            style={{ fontSize: 10, letterSpacing: 0.4 }} data-testid={`import-status-${status}`}>{STATUS_LABEL[status] || status}</span>
      {superseded && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded border bg-surface-2 text-ink-3 border-line font-mono uppercase"
              style={{ fontSize: 10, letterSpacing: 0.4 }} data-testid="import-superseded-chip">superseded</span>
      )}
    </span>
  );
}

export function CountChips({ counts }) {
  return (
    <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }} data-testid="import-counts">
      {COUNT_ORDER.map((k) => `${counts?.[k] ?? 0} ${k}`).join(" · ")}
    </span>
  );
}

function groupBySupplier(batches) {
  const groups = new Map();
  for (const b of batches) {
    const label = b.supplier_label || "No supplier";
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(b);
  }
  const tail = ["Multiple suppliers", "No supplier"];
  return Array.from(groups.entries()).sort(([a], [b]) => {
    const ta = tail.indexOf(a), tb = tail.indexOf(b);
    if (ta !== tb) return (ta === -1 ? -1 : ta) - (tb === -1 ? -1 : tb);
    return a.localeCompare(b);
  });
}

/* LT-19b §11 — the list, grouped under a supplier heading, newest first. */
export function ImportBatchList({ batches, totalIncludingArchived, showArchived, onToggleArchived, onOpen, onDownload, onDiscard, onArchive, onUnarchive }) {
  const groups = useMemo(() => groupBySupplier(batches || []), [batches]);
  const hiddenArchived = Math.max(0, (totalIncludingArchived || 0) - (batches || []).length);

  return (
    <div className="space-y-4" data-testid="import-batch-list">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Imports</h3>
        <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => onToggleArchived(e.target.checked)} data-testid="import-show-archived" />
          Show archived
        </label>
      </div>
      {groups.length === 0 && (
        <Card>
          {totalIncludingArchived > 0
            ? <EmptyState icon={Archive} title="No active imports" hint={`${totalIncludingArchived} archived import${totalIncludingArchived === 1 ? "" : "s"} hidden — tick “Show archived” to see them.`} />
            : <EmptyState icon={FileText} title="No imports yet" hint="Upload a supplier price list above. You will see a full preview before anything is written." />}
        </Card>
      )}
      {groups.map(([label, rows]) => (
        <Card key={label}>
          <div className="px-4 py-2 border-b border-line bg-surface-2/40 font-medium text-ink" style={{ fontSize: 12.5 }} data-testid={`import-group-${label}`}>
            {label}
          </div>
          <table className="w-full" style={{ fontSize: 12.5 }}>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-t border-line-soft hover:bg-surface-2/40" data-testid={`import-row-${b.id}`}>
                  <td className="px-4 py-2 text-ink-3 font-mono tnum whitespace-nowrap" style={{ fontSize: 11.5 }}>{ukDate(b.uploaded_at)}</td>
                  <td className="px-2 py-2">
                    <button type="button" onClick={() => onOpen(b)} className="text-ink hover:underline text-left" data-testid={`import-open-${b.id}`}>{b.filename}</button>
                    {b.archived_at && <span className="ml-2 text-ink-4" style={{ fontSize: 11 }}>archived {ukDate(b.archived_at)}</span>}
                  </td>
                  <td className="px-2 py-2 text-ink-3 whitespace-nowrap" style={{ fontSize: 11.5 }}>effective {ukDate(b.effective_from)}</td>
                  <td className="px-2 py-2"><StatusChip status={b.status} superseded={b.superseded} /></td>
                  <td className="px-2 py-2"><CountChips counts={b.counts} /></td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <QuietButton icon={FileText} onClick={() => onOpen(b)} data-testid={`import-view-${b.id}`}>View</QuietButton>
                    <QuietButton icon={Download} onClick={() => onDownload(b)} className="ml-1" data-testid={`import-download-${b.id}`}>Original</QuietButton>
                    {(b.status === "draft" || b.status === "failed") && (
                      <QuietButton icon={Trash2} onClick={() => onDiscard(b)} className="ml-1" data-testid={`import-discard-${b.id}`}>Discard</QuietButton>
                    )}
                    {b.status !== "draft" && !b.archived_at && (
                      <QuietButton icon={Archive} onClick={() => onArchive(b)} className="ml-1" data-testid={`import-archive-${b.id}`}>Archive</QuietButton>
                    )}
                    {b.archived_at && (
                      <QuietButton icon={ArchiveRestore} onClick={() => onUnarchive(b)} className="ml-1" data-testid={`import-unarchive-${b.id}`}>Unarchive</QuietButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
      {!showArchived && hiddenArchived > 0 && groups.length > 0 && (
        <div className="text-ink-4" style={{ fontSize: 11.5 }} data-testid="import-archived-hidden-note">{hiddenArchived} archived hidden.</div>
      )}
    </div>
  );
}
